/**
 * ErrorBoundaryTestHarness - E2E test utility for ErrorBoundary
 *
 * This component is only active in test mode (localStorage flag set).
 * It listens for custom events to trigger React errors that will be
 * caught by the ErrorBoundary and show the CrashDialog.
 */
import { useState, useEffect } from 'react';

/** Check if we're in test mode */
const isTestMode = (): boolean => {
  // Prefer preload-provided flag (stable, avoids localStorage timing races)
  if (typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode) {
    return window.electronAPI.testMode.isTestMode();
  }

  return localStorage.getItem('sql-studio-skip-session-dialog') === '1';
};

/**
 * Test harness component that can throw errors on demand
 * Include this inside your React tree to test ErrorBoundary behavior
 */
export function ErrorBoundaryTestHarness() {
  const [shouldThrow, setShouldThrow] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Test error from ErrorBoundaryTestHarness');

  useEffect(() => {
    // Only set up listeners in test mode
    if (!isTestMode()) return;

    const handleTriggerError = (event: CustomEvent<{ message?: string }>) => {
      const message = event.detail?.message || 'Test error from ErrorBoundaryTestHarness';
      setErrorMessage(message);
      setShouldThrow(true);
    };

    const handleResetError = () => {
      setShouldThrow(false);
    };

    window.addEventListener('test-trigger-error', handleTriggerError as EventListener);
    window.addEventListener('test-reset-error', handleResetError);

    return () => {
      window.removeEventListener('test-trigger-error', handleTriggerError as EventListener);
      window.removeEventListener('test-reset-error', handleResetError);
    };
  }, []);

  // Don't render anything if not in test mode
  if (!isTestMode()) {
    return null;
  }

  // Throw error during render if triggered
  if (shouldThrow) {
    throw new Error(errorMessage);
  }

  return null;
}
